import { useMemo, useState } from "react";
import {
  type BuilderState,
  type Engine,
  type Mode,
  type Preset,
  defaultState,
  presets,
} from "@/lib/builder-state";
import {
  generate,
  connectionStrings,
  defaultPortFor,
  engineList,
  isSafeIdent,
  capsFor,
} from "@/lib/dialects";

const PRESET_LABELS: Record<Preset, string> = {
  readonly: "Read only",
  readwrite: "Read / Write",
  app_owner: "App owner",
  migrations: "Migrations",
  custom: "Custom",
};

function genPassword(length = 28): string {
  const charset = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789-_=+@#%";
  const arr = new Uint32Array(length);
  crypto.getRandomValues(arr);
  let out = "";
  for (let i = 0; i < length; i++) out += charset[arr[i] % charset.length];
  return out;
}

export function QueryBuilder() {
  const [s, setS] = useState<BuilderState>(defaultState);
  const [copied, setCopied] = useState(false);

  const update = <K extends keyof BuilderState>(k: K, v: BuilderState[K]) =>
    setS((p) => ({ ...p, [k]: v }));

  const updateEngine = (v: Engine) => {
    setS((p) => ({ ...p, engine: v, port: defaultPortFor(v) }));
  };

  const applyPreset = (p: Preset) => {
    if (p === "custom") return setS((prev) => ({ ...prev, preset: p }));
    setS((prev) => ({ ...prev, ...presets[p], preset: p }));
  };

  const sql = useMemo(() => generate(s), [s]);
  const caps = capsFor(s.engine);

  const warnings: string[] = [];
  if (!isSafeIdent(s.dbName))
    warnings.push("Database name has unusual characters — review the quoted output.");
  if (!isSafeIdent(s.userName))
    warnings.push("User name has unusual characters — review the quoted output.");
  if (s.engine === "postgres" && !s.password && s.mode === "create" && s.canLogin)
    warnings.push(
      "No password set for a login role. Set one or use external auth (peer/md5/SCRAM).",
    );
  if (s.superuser)
    warnings.push("SUPERUSER bypasses all checks — do not use for application accounts.");
  if (s.engine === "mysql" && s.hostPattern === "%")
    warnings.push(
      "Host pattern '%' allows connections from anywhere. Restrict to specific hosts in production.",
    );

  const copy = async () => {
    await navigator.clipboard.writeText(sql);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const download = () => {
    const blob = new Blob([sql], { type: "text/sql" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${s.dbName || "schema"}_${s.userName || "user"}.sql`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen px-4 py-8 md:px-8 md:py-12">
      <div className="mx-auto max-w-7xl">
        <Header />

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
          {/* LEFT — controls */}
          <div className="space-y-6">
            <Card title="Engine & mode" accent="primary">
              <Field label="Database engine">
                <Select
                  value={s.engine}
                  onChange={(v) => updateEngine(v as Engine)}
                  options={engineList.map((e) => ({ value: e.id, label: e.label }))}
                />
              </Field>
              <Field label="Action">
                <Tabs<Mode>
                  value={s.mode}
                  onChange={(v) => update("mode", v)}
                  options={[
                    { value: "create", label: "Create new" },
                    { value: "update", label: "Update existing" },
                  ]}
                />
              </Field>
              <p className="text-xs text-muted-foreground">
                {s.mode === "update"
                  ? "Update mode overrides role attributes and re-applies grants. Existing rows are not touched."
                  : "Create mode uses IF NOT EXISTS guards where supported."}
              </p>
            </Card>

            <Card title="Database" accent="secondary">
              <Field label="Database name">
                <Input
                  value={s.dbName}
                  onChange={(v) => update("dbName", v)}
                  placeholder="app_db"
                />
              </Field>
              {caps.pgDatabaseOptions && (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Encoding">
                    <Input value={s.encoding} onChange={(v) => update("encoding", v)} />
                  </Field>
                  <Field label="Locale (LC_*)">
                    <Input value={s.locale} onChange={(v) => update("locale", v)} />
                  </Field>
                  <Field label="Template">
                    <Select
                      value={s.template}
                      onChange={(v) => update("template", v)}
                      options={[
                        { value: "template0", label: "template0 (clean)" },
                        { value: "template1", label: "template1 (default)" },
                      ]}
                    />
                  </Field>
                  <Checkbox
                    label="User owns the database"
                    checked={s.ownDatabase}
                    onChange={(v) => update("ownDatabase", v)}
                  />
                </div>
              )}
            </Card>

            <Card title="User / role" accent="accent">
              <Field label="User name">
                <Input
                  value={s.userName}
                  onChange={(v) => update("userName", v)}
                  placeholder="app_user"
                />
              </Field>
              <Field label="Password">
                <div className="flex gap-2">
                  <Input
                    value={s.password}
                    onChange={(v) => update("password", v)}
                    placeholder="•••••••• (set or generate)"
                    type="text"
                    mono
                  />
                  <button
                    type="button"
                    onClick={() => update("password", genPassword())}
                    className="brutal-sm bg-primary px-3 py-2 text-sm font-bold hover:translate-x-[-1px] hover:translate-y-[-1px] hover:shadow-[3px_3px_0_0_var(--color-border)] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none"
                  >
                    Generate
                  </button>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Generated locally with crypto.getRandomValues — nothing leaves the browser.
                </p>
              </Field>

              {caps.hostPattern && (
                <Field label="Host pattern">
                  <Input
                    value={s.hostPattern}
                    onChange={(v) => update("hostPattern", v)}
                    placeholder="%"
                    mono
                  />
                </Field>
              )}

              <div className="grid grid-cols-2 gap-3">
                {caps.connectionLimit && (
                  <Field label="Connection limit">
                    <Input
                      type="number"
                      value={String(s.connectionLimit)}
                      onChange={(v) => update("connectionLimit", parseInt(v || "-1", 10))}
                    />
                  </Field>
                )}
                {caps.validUntil && (
                  <Field label="Valid until">
                    <Input
                      type="date"
                      value={s.validUntil}
                      onChange={(v) => update("validUntil", v)}
                    />
                  </Field>
                )}
              </div>

              {caps.pgRoleAttributes && (
                <div className="space-y-2 pt-2">
                  <p className="text-xs font-bold uppercase tracking-wider">Role attributes</p>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                    <Checkbox
                      label="LOGIN"
                      checked={s.canLogin}
                      onChange={(v) => update("canLogin", v)}
                    />
                    <Checkbox
                      label="INHERIT"
                      checked={s.inheritRole}
                      onChange={(v) => update("inheritRole", v)}
                    />
                    <Checkbox
                      label="CREATEDB"
                      checked={s.createDb}
                      onChange={(v) => update("createDb", v)}
                    />
                    <Checkbox
                      label="CREATEROLE"
                      checked={s.createRole}
                      onChange={(v) => update("createRole", v)}
                    />
                    <Checkbox
                      label="REPLICATION"
                      checked={s.replication}
                      onChange={(v) => update("replication", v)}
                    />
                    <Checkbox
                      label="BYPASSRLS"
                      checked={s.bypassRls}
                      onChange={(v) => update("bypassRls", v)}
                    />
                    {caps.superuserToggle && (
                      <Checkbox
                        label="SUPERUSER (danger)"
                        checked={s.superuser}
                        onChange={(v) => update("superuser", v)}
                        danger
                      />
                    )}
                  </div>
                </div>
              )}

              {s.mode === "update" && (
                <div className="space-y-2 pt-2">
                  <Checkbox
                    label="Reset password"
                    checked={s.resetPassword}
                    onChange={(v) => update("resetPassword", v)}
                  />
                  <Checkbox
                    label="Revoke existing grants before re-applying"
                    checked={s.revokeBeforeGrant}
                    onChange={(v) => update("revokeBeforeGrant", v)}
                  />
                  {caps.pgUpdateHints && (
                    <Checkbox
                      label="Include REASSIGN / DROP OWNED hints"
                      checked={s.dropOwnedReassign}
                      onChange={(v) => update("dropOwnedReassign", v)}
                    />
                  )}
                </div>
              )}
            </Card>

            {caps.schemaIsolation && (
              <Card title="Schema isolation" accent="secondary">
                <Checkbox
                  label="Create a dedicated schema for this user"
                  checked={s.createSchema}
                  onChange={(v) => update("createSchema", v)}
                />
                {s.createSchema && (
                  <>
                    <Field label="Schema name">
                      <Input value={s.schemaName} onChange={(v) => update("schemaName", v)} />
                    </Field>
                    <Checkbox
                      label="Set search_path to this schema for the role"
                      checked={s.setSearchPath}
                      onChange={(v) => update("setSearchPath", v)}
                    />
                  </>
                )}
                <Checkbox
                  label="REVOKE all from PUBLIC on schema public"
                  checked={s.revokePublicSchema}
                  onChange={(v) => update("revokePublicSchema", v)}
                />
                <Checkbox
                  label="REVOKE CONNECT from PUBLIC on this database"
                  checked={s.revokeOtherDbConnect}
                  onChange={(v) => update("revokeOtherDbConnect", v)}
                />
                <Checkbox
                  label="Include pg_hba.conf hint to hide other DBs"
                  checked={s.hideOtherDatabases}
                  onChange={(v) => update("hideOtherDatabases", v)}
                />
              </Card>
            )}

            {caps.schemaToggle && (
              <Card title="Schema" accent="secondary">
                <Checkbox
                  label="Create a dedicated schema for this user"
                  checked={s.createSchema}
                  onChange={(v) => update("createSchema", v)}
                />
                {s.createSchema && (
                  <Field label="Schema name">
                    <Input value={s.schemaName} onChange={(v) => update("schemaName", v)} />
                  </Field>
                )}
              </Card>
            )}

            <Card title="Privileges" accent="primary">
              <Field label="Preset">
                <Tabs<Preset>
                  value={s.preset}
                  onChange={applyPreset}
                  options={(Object.keys(PRESET_LABELS) as Preset[]).map((p) => ({
                    value: p,
                    label: PRESET_LABELS[p],
                  }))}
                  compact
                />
              </Field>

              <div className="space-y-2">
                <p className="text-xs font-bold uppercase tracking-wider">Tables</p>
                <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                  <Checkbox
                    label="SELECT"
                    checked={s.privSelect}
                    onChange={(v) => update("privSelect", v)}
                  />
                  <Checkbox
                    label="INSERT"
                    checked={s.privInsert}
                    onChange={(v) => update("privInsert", v)}
                  />
                  <Checkbox
                    label="UPDATE"
                    checked={s.privUpdate}
                    onChange={(v) => update("privUpdate", v)}
                  />
                  <Checkbox
                    label="DELETE"
                    checked={s.privDelete}
                    onChange={(v) => update("privDelete", v)}
                  />
                  {caps.truncatePriv && (
                    <Checkbox
                      label="TRUNCATE"
                      checked={s.privTruncate}
                      onChange={(v) => update("privTruncate", v)}
                    />
                  )}
                  {caps.referencesAndTrigger && (
                    <>
                      <Checkbox
                        label="REFERENCES"
                        checked={s.privReferences}
                        onChange={(v) => update("privReferences", v)}
                      />
                      <Checkbox
                        label="TRIGGER"
                        checked={s.privTrigger}
                        onChange={(v) => update("privTrigger", v)}
                      />
                    </>
                  )}
                </div>
              </div>

              {caps.sequences && (
                <div className="space-y-2 pt-2">
                  <p className="text-xs font-bold uppercase tracking-wider">Sequences</p>
                  <div className="grid grid-cols-3 gap-x-3 gap-y-2">
                    <Checkbox
                      label="USAGE"
                      checked={s.privUsageSeq}
                      onChange={(v) => update("privUsageSeq", v)}
                    />
                    <Checkbox
                      label="SELECT"
                      checked={s.privSelectSeq}
                      onChange={(v) => update("privSelectSeq", v)}
                    />
                    <Checkbox
                      label="UPDATE"
                      checked={s.privUpdateSeq}
                      onChange={(v) => update("privUpdateSeq", v)}
                    />
                  </div>
                </div>
              )}
              {caps.schemaAndFunctions && (
                <div className="space-y-2 pt-2">
                  <p className="text-xs font-bold uppercase tracking-wider">Schema & functions</p>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                    <Checkbox
                      label="USAGE on schema"
                      checked={s.privUsageSchema}
                      onChange={(v) => update("privUsageSchema", v)}
                    />
                    <Checkbox
                      label="CREATE on schema"
                      checked={s.privCreateSchema}
                      onChange={(v) => update("privCreateSchema", v)}
                    />
                    <Checkbox
                      label="EXECUTE on functions"
                      checked={s.privExecuteFn}
                      onChange={(v) => update("privExecuteFn", v)}
                    />
                  </div>
                </div>
              )}
              {caps.defaultPrivileges && (
                <div className="space-y-2 pt-2">
                  <Checkbox
                    label="ALTER DEFAULT PRIVILEGES for future objects"
                    checked={s.defaultPrivileges}
                    onChange={(v) => update("defaultPrivileges", v)}
                  />
                  {s.defaultPrivileges && (
                    <Field label="FOR ROLE (object creator, optional)">
                      <Input
                        value={s.defaultPrivilegesFor}
                        onChange={(v) => update("defaultPrivilegesFor", v)}
                        placeholder="leave empty for current user"
                      />
                    </Field>
                  )}
                </div>
              )}
            </Card>

            <Card title="Output options" accent="accent">
              {caps.wrapTransaction && (
                <Checkbox
                  label="Wrap in BEGIN / COMMIT"
                  checked={s.wrapTransaction}
                  onChange={(v) => update("wrapTransaction", v)}
                />
              )}
              <Checkbox
                label="Include comments & notes"
                checked={s.includeComments}
                onChange={(v) => update("includeComments", v)}
              />
              {caps.quoteIdentifiers && (
                <Checkbox
                  label="Always quote identifiers"
                  checked={s.quoteIdentifiers}
                  onChange={(v) => update("quoteIdentifiers", v)}
                />
              )}
            </Card>

            <Card title="Connection" accent="primary">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Host">
                  <Input
                    value={s.host}
                    onChange={(v) => update("host", v)}
                    placeholder="localhost"
                  />
                </Field>
                <Field label="Port">
                  <Input
                    type="number"
                    value={String(s.port)}
                    onChange={(v) => update("port", parseInt(v || "0", 10))}
                  />
                </Field>
              </div>
              <p className="text-xs text-muted-foreground">
                Used to build connection strings on the right. Port auto-switches with engine.
              </p>
            </Card>
          </div>

          {/* RIGHT — output */}
          <div className="lg:sticky lg:top-6 lg:self-start">
            <div className="brutal-lg bg-card">
              <div className="flex items-center justify-between border-b-2 border-border bg-primary px-4 py-3">
                <div className="flex items-center gap-2">
                  <Dot />
                  <span className="font-bold uppercase tracking-wider">generated.sql</span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={copy}
                    className="brutal-sm bg-card px-3 py-1.5 text-sm font-bold hover:translate-x-[-1px] hover:translate-y-[-1px] hover:shadow-[3px_3px_0_0_var(--color-border)] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none"
                  >
                    {copied ? "Copied ✓" : "Copy"}
                  </button>
                  <button
                    onClick={download}
                    className="brutal-sm bg-accent px-3 py-1.5 text-sm font-bold hover:translate-x-[-1px] hover:translate-y-[-1px] hover:shadow-[3px_3px_0_0_var(--color-border)] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none"
                  >
                    Download
                  </button>
                </div>
              </div>

              {warnings.length > 0 && (
                <ul className="border-b-2 border-border bg-destructive/10 px-4 py-3 text-sm">
                  {warnings.map((w, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="font-bold text-destructive">!</span>
                      <span>{w}</span>
                    </li>
                  ))}
                </ul>
              )}

              <pre className="max-h-[70vh] overflow-auto bg-[color:var(--color-code-bg)] p-4 text-xs leading-relaxed text-[color:var(--color-code-punct)]">
                <Highlighted code={sql} />
              </pre>
            </div>

            <p className="mt-3 text-xs text-muted-foreground">
              All generation happens in your browser. Nothing is sent anywhere.
            </p>

            <ConnectionStringPanel state={s} />
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
}

/* ---------------- subcomponents ---------------- */

function Header() {
  return (
    <header className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
      <div>
        <div className="brutal-sm inline-block bg-accent px-2 py-1 text-xs font-bold uppercase tracking-widest">
          DB User Forge
        </div>
        <h1 className="mt-3 text-4xl font-extrabold leading-none tracking-tight md:text-5xl">
          Hardened SQL
          <br />
          for users & databases.
        </h1>
        <p className="mt-3 max-w-xl text-sm text-muted-foreground">
          Generate least-privilege SQL to create or update databases, roles, schemas and grants —
          Postgres-first, with MySQL, Oracle, SQL Server, MongoDB and Db2 support.
        </p>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="mt-12 border-t-2 border-border pt-4 text-xs text-muted-foreground">
      <p>
        Postgres best practices follow the official docs:{" "}
        <span className="font-mono">CREATE ROLE</span>, <span className="font-mono">GRANT</span>,{" "}
        <span className="font-mono">ALTER DEFAULT PRIVILEGES</span>. Always review generated SQL
        before running against production.
      </p>
      <p className="mt-2">
        Created by{" "}
        <a
          href="https://github.com/codedoga"
          target="_blank"
          rel="noreferrer"
          className="font-mono font-medium text-foreground underline-offset-4 hover:underline"
        >
          codedoga
        </a>
        .
      </p>
    </footer>
  );
}

function Card({
  title,
  accent,
  children,
}: {
  title: string;
  accent: "primary" | "secondary" | "accent";
  children: React.ReactNode;
}) {
  const bg =
    accent === "primary" ? "bg-primary" : accent === "secondary" ? "bg-secondary" : "bg-accent";
  return (
    <section className="brutal bg-card">
      <div className={`flex items-center gap-2 border-b-2 border-border ${bg} px-4 py-2`}>
        <Dot />
        <h2 className="text-sm font-extrabold uppercase tracking-wider">{title}</h2>
      </div>
      <div className="space-y-3 p-4">{children}</div>
    </section>
  );
}

function Dot() {
  return <span className="inline-block h-3 w-3 rounded-full border-2 border-border bg-card" />;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-bold uppercase tracking-wider">{label}</span>
      {children}
    </label>
  );
}

function Input({
  value,
  onChange,
  placeholder,
  type = "text",
  mono,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  mono?: boolean;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`brutal-flat w-full bg-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring ${
        mono ? "font-mono" : ""
      }`}
    />
  );
}

function Select({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="brutal-flat w-full bg-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function Tabs<T extends string>({
  value,
  onChange,
  options,
  compact,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
  compact?: boolean;
}) {
  return (
    <div className="flex flex-wrap gap-0 brutal-flat">
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={`flex-1 border-r-2 border-border px-3 ${compact ? "py-1.5 text-xs" : "py-2 text-sm"} font-bold last:border-r-0 ${
              active ? "bg-primary" : "bg-card hover:bg-muted"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function Checkbox({
  label,
  checked,
  onChange,
  danger,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  danger?: boolean;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm">
      <span
        className={`brutal-flat flex h-5 w-5 items-center justify-center ${
          checked
            ? danger
              ? "bg-destructive text-destructive-foreground"
              : "bg-allow"
            : "bg-input"
        }`}
      >
        {checked && (
          <svg
            viewBox="0 0 16 16"
            className="h-3.5 w-3.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
          >
            <path d="M3 8l3.5 3.5L13 5" strokeLinecap="square" />
          </svg>
        )}
      </span>
      <input
        type="checkbox"
        className="sr-only"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className={danger ? "font-bold text-destructive" : ""}>{label}</span>
    </label>
  );
}

function Highlighted({ code }: { code: string }) {
  const lines = code.split("\n");
  return (
    <code className="block">
      {lines.map((line, i) => (
        <div key={i} className="whitespace-pre">
          {renderLine(line)}
        </div>
      ))}
    </code>
  );
}

function ConnectionStringPanel({ state }: { state: BuilderState }) {
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
  const strings = useMemo(() => connectionStrings(state), [state]);

  const copy = async (url: string, idx: number) => {
    await navigator.clipboard.writeText(url);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx((c) => (c === idx ? null : c)), 1500);
  };

  return (
    <div className="brutal-lg mt-6 bg-card">
      <div className="flex items-center gap-2 border-b-2 border-border bg-secondary px-4 py-3">
        <Dot />
        <span className="font-bold uppercase tracking-wider">Connection strings</span>
      </div>
      <div className="space-y-3 p-4">
        {strings.map((cs, i) => (
          <div key={i} className="space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                {cs.label}
              </span>
              <button
                onClick={() => copy(cs.url, i)}
                className="brutal-sm bg-card px-2 py-0.5 text-xs font-bold hover:translate-x-[-1px] hover:translate-y-[-1px] hover:shadow-[2px_2px_0_0_var(--color-border)] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none"
              >
                {copiedIdx === i ? "Copied ✓" : "Copy"}
              </button>
            </div>
            <code className="block break-all rounded-sm bg-[color:var(--color-code-bg)] px-3 py-2 text-xs leading-relaxed text-[color:var(--color-code-string)]">
              {cs.url}
            </code>
            {cs.note && <p className="text-xs text-muted-foreground">{cs.note}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

const KEYWORDS = new Set([
  "CREATE",
  "ROLE",
  "DATABASE",
  "SCHEMA",
  "USER",
  "GRANT",
  "REVOKE",
  "ALTER",
  "DEFAULT",
  "PRIVILEGES",
  "ON",
  "TO",
  "FROM",
  "IN",
  "FOR",
  "WITH",
  "IF",
  "NOT",
  "EXISTS",
  "BEGIN",
  "COMMIT",
  "DO",
  "END",
  "SELECT",
  "INSERT",
  "UPDATE",
  "DELETE",
  "TRUNCATE",
  "REFERENCES",
  "TRIGGER",
  "USAGE",
  "EXECUTE",
  "ALL",
  "TABLES",
  "SEQUENCES",
  "FUNCTIONS",
  "PUBLIC",
  "OWNER",
  "AUTHORIZATION",
  "ENCODING",
  "TEMPLATE",
  "CONNECTION",
  "LIMIT",
  "PASSWORD",
  "VALID",
  "UNTIL",
  "LOGIN",
  "NOLOGIN",
  "SUPERUSER",
  "NOSUPERUSER",
  "CREATEDB",
  "NOCREATEDB",
  "CREATEROLE",
  "NOCREATEROLE",
  "REPLICATION",
  "NOREPLICATION",
  "BYPASSRLS",
  "NOBYPASSRLS",
  "INHERIT",
  "NOINHERIT",
  "CONNECT",
  "REASSIGN",
  "OWNED",
  "BY",
  "CURRENT_USER",
  "PRAGMA",
  "SET",
  "search_path",
  "CHARACTER",
  "COLLATE",
  "IDENTIFIED",
  "FLUSH",
  "PRIVILEGES",
  "MAX_USER_CONNECTIONS",
  "EXPIRE",
  "INTERVAL",
  "DAY",
  "DROP",
  "REFERENCES",
  "INDEX",
  "LC_COLLATE",
  "LC_CTYPE",
]);

function renderLine(line: string) {
  if (line.trim().startsWith("--")) {
    return <span className="text-[color:var(--color-code-comment)]">{line}</span>;
  }
  const parts: React.ReactNode[] = [];
  const regex =
    /('[^']*')|("[^"]*")|(`[^`]*`)|(\b[A-Za-z_][A-Za-z0-9_]*\b)|(\d+)|([^A-Za-z0-9_'"`]+)/g;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = regex.exec(line)) !== null) {
    const [tok, sq, dq, bq, ident, num, other] = m;
    if (sq || dq || bq) {
      parts.push(
        <span key={key++} className="text-[color:var(--color-code-string)]">
          {tok}
        </span>,
      );
    } else if (ident) {
      if (KEYWORDS.has(ident.toUpperCase()) || KEYWORDS.has(ident)) {
        parts.push(
          <span key={key++} className="font-bold text-[color:var(--color-code-key)]">
            {ident}
          </span>,
        );
      } else {
        parts.push(<span key={key++}>{ident}</span>);
      }
    } else if (num) {
      parts.push(
        <span key={key++} className="text-[color:var(--color-code-num)]">
          {num}
        </span>,
      );
    } else {
      parts.push(<span key={key++}>{other}</span>);
    }
  }
  return parts;
}
